import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isNaN, throttle } from 'lodash';
import { RouteComponentProps } from 'react-router-dom';
import {
  ATMButton,
  ATMContainer,
  ATMGrid,
  ATMLoader,
} from 'shared-it-appmod-ui';
import {
  ToastError,
  ToastSuccess,
} from 'src/components/atoms/toaster/toaster.component';
import {
  AccessType,
  DroneInternalFlightType,
  FlightCategory,
  RequestStatus,
} from 'src/constants';
import { useCNDDistrictContext } from 'src/contexts/cnd-districts.context';
import { useRequestContext } from 'src/contexts/request.context';
import { actionTypes } from 'src/ducks/request.duck';
import history from 'src/history';
import { getIsAdmin } from 'src/libraries/access.library';
import { getUser } from 'src/libraries/auth.library';
import Lang from 'src/libraries/language';
import { IRequestForm } from 'src/models/request.model';
import {
  getRequestStatus,
  getRequestUrl,
  isRequestAccessible,
} from 'src/selectors/request.selector';
import Moment from 'src/libraries/moment.library';
import Error404 from '../../error/error-404/error-404.component';
import FlightRequestChangeStatus from '../flight-request-change-status/flight-request-change-status.component';
import FlightRequestForm from '../flight-request-form/flight-request-form.component';
import FlightRequestHeader from '../flight-request-header/flight-request-header.component';
import styles from './flight-request-edit.module.scss';

const DRONE_CGI_FLIGHT_TYPES = new Set(
  Object.values(DroneInternalFlightType).map((value) => value.toLowerCase())
);

export type IProps = RouteComponentProps<{
  id: string;
  type: string;
}>;

const FlightRequestEdit: React.FC<IProps> = ({ match }) => {
  const requestId = Number(match.params.id);
  const type = String(match.params.type);
  const { state, actions }: any = useRequestContext();
  const { state: cndDistrictState } = useCNDDistrictContext();
  const shouldReturn = useRef<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [isConfirm, setIsConfirm] = useState<IRequestForm>();
  const [isPast, setIsPast] = useState(false);
  const status = getRequestStatus(state, actionTypes.REQUEST_DATA_FETCH);

  useEffect(() => {
    if (getIsAdmin()) {
      document.querySelector('#admin-section-form')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  useEffect(() => {
    actions.dataGET(requestId, type);
  }, [requestId, type, actions]);

  useEffect(() => {
    if (
      state.data &&
      requestId &&
      state.data?.request?.requestId !== requestId
    ) {
      actions.setRequestData();
    }
    if (
      Moment(state.data?.request.flightDate).isBefore(
        Moment(Moment().format('MM/DD/YYYY'))
      )
    ) {
      setIsPast(true);
    } else {
      setIsPast(false);
    }
  }, [requestId, state.data, actions, setIsPast]);

  const handleClick = useCallback(
    (close) => {
      shouldReturn.current = close;
      if (formRef && formRef.current) {
        formRef.current.handleSubmit();
      }
    },
    [formRef, shouldReturn]
  );

  const handleSubmit = useCallback(
    throttle(async (data: IRequestForm) => {
      const statusConfirm = [RequestStatus.Declined];
      if (state?.data?.request?.requestStatus !== RequestStatus.Cancelled) {
        statusConfirm.push(RequestStatus.Cancelled);
      }
      if (
        !isConfirm &&
        data.request?.requestStatus &&
        statusConfirm.includes(data.request?.requestStatus as RequestStatus)
      ) {
        setIsConfirm(data);
        return;
      }

      const formData = {
        ...data,
        request: {
          ...data.request,
          flightDate: Moment(data.request?.flightDate).format('YYYY/MM/DD'),
          flightEndDate: data.request?.flightEndDate
            ? Moment.tz(
                Moment(data.request.flightEndDate).format('YYYY-MM-DD'),
                'YYYY-MM-DD',
                'America/Los_Angeles'
              ).utc().format('YYYY-MM-DDTHH:mm:ss')
            : null,
        },
      };

      if (formData && formData.request) {
        let result;
        let switchValue = formData.request.flightFlag;
        if (state?.data?.passengerFlight?.isOldSCG) {
          switchValue = 6;
        }
        switch (switchValue) {
          case FlightCategory.PatrolPassenger: {
            if (state?.data?.passengerFlight?.isOldFlight) {
              result = await actions.updatePatrolPassengerContractorPUT(
                requestId,
                formData
              );
            } else {
              result = await actions.updatePatrolPassengerPUT(
                requestId,
                formData
              );
            }
            break;
          }

          case FlightCategory.ContractorPatrol: {
            result = await actions.updatePatrolPassengerContractorPUT(
              requestId,
              formData
            );

            break;
          }

          case FlightCategory.ConstructionWork: {
            result = await actions.updateConstructionPUT(requestId, formData);

            break;
          }

          case FlightCategory.ContractorConstruction: {
            result = await actions.updateConstructionContractorPUT(
              requestId,
              formData
            );

            break;
          }

          case FlightCategory.DroneFlightInternal: {
            result = await actions.updateDroneInternalPUT(requestId, formData);

            let cndResponse;
            let cndStatus = '';
            const normalizeCndError = (rawError: any) => {
              let htmlError = '';
              if (typeof rawError === 'string') {
                htmlError = rawError;
              } else if (typeof rawError?.error === 'string') {
                htmlError = rawError.error;
              } else if (typeof rawError?.data === 'string') {
                htmlError = rawError.data;
              }

              if (!htmlError || !/<!doctype html>|<html/i.test(htmlError)) {
                return rawError;
              }

              const statusMatch = htmlError.match(/HttpStatusCode:\s*(\d+)/i);
              const codeMatch = htmlError.match(/ErrorCode\s*:\s*([^<\n]+)/i);
              const messageMatch = htmlError.match(/<h1>(.*?)<\/h1>/i);
              const requestIdMatch = htmlError.match(
                /RequestId\s*:\s*([^<\n]+)/i
              );
              const timestampMatch = htmlError.match(
                /TimeStamp\s*:\s*([^<\n]+)/i
              );
              const statusCode = Number(
                statusMatch?.[1] || rawError?.status || 500
              );

              return {
                statusCode,
                error: String(codeMatch?.[1] || 'UnknownError').trim(),
                message: String(
                  messageMatch?.[1] ||
                    'Request failed with HTML error response.'
                ).trim(),
                requestId:
                  String(requestIdMatch?.[1] || '').trim() || undefined,
                timestamp:
                  String(timestampMatch?.[1] || '').trim() || undefined,
              };
            };
            const updatedDroneFlightSDGE = result?.payload?.droneFlightSDGE || {};
            const updatedRequest = result?.payload?.request || {};
            const droneFlightSDGEData =
              (formData as any)?.droneFlightSDGE ||
              state?.data?.droneFlightSDGE ||
              {};
            const cndWONumbers =
              (updatedDroneFlightSDGE?.cndWONumbers as Array<any>) ||
              (droneFlightSDGEData?.cndWONumbers as Array<any>) ||
              [];

            const workOrders = cndWONumbers
              .map((item: any) => String(item?.cndWONumber || '').trim())
              .filter((value) => value);
            const selectedDistrict = String(
              updatedDroneFlightSDGE?.cndDistrict ||
              droneFlightSDGEData?.cndDistrict || ''
            ).trim();

            const matchedDistrict = (cndDistrictState?.list ?? []).find(
              (district) =>
                String(district?.cndDistrict || '')
                  .trim()
                  .toUpperCase() === selectedDistrict.toUpperCase()
            );

            const dist3 = String(
              matchedDistrict?.dist3 || selectedDistrict || ''
            )
              .trim()
              .toUpperCase();
            const flightType = String(
              updatedDroneFlightSDGE?.flightType ||
                droneFlightSDGEData?.flightType ||
                ''
            )
              .trim()
              .toLowerCase();
            const flightId =
              updatedDroneFlightSDGE?.flightId ||
              droneFlightSDGEData?.flightId ||
              state?.data?.droneFlightSDGE?.flightId;
            const effectiveRequestStatus =
              updatedRequest?.requestStatus || formData.request.requestStatus;
            const isApproved =
              effectiveRequestStatus === RequestStatus.Approved;

            const payloadCNDAPIData = {
              JobId: flightId ? `DRO${flightId}` : '',
              WorkOrders: workOrders,
              District: dist3,
              Company: 'SCG',
              TaskType: 'Drone',
              StartDate: Moment(data.request?.flightDate).format('MM/DD/YYYY'),
            };

            const hasPayloadCNDAPIData =
              !!payloadCNDAPIData.JobId &&
              Array.isArray(payloadCNDAPIData.WorkOrders) &&
              payloadCNDAPIData.WorkOrders.length > 0 &&
              !!payloadCNDAPIData.District;

            const cndPrecheck = {
              hasUpdateSuccess: !result?.error,
              hasPayloadCNDAPIData,
              hasFlightId: !!flightId,
              isApproved,
              hasWorkOrders: workOrders.length > 0,
              hasDistrict: !!dist3,
              isDroneCgi: DRONE_CGI_FLIGHT_TYPES.has(flightType),
            };

            // const skipReasons = Object.entries(cndPrecheck)
            //   .filter(([, ok]) => !ok)
            //   .map(([key]) => key);

            if (
              cndPrecheck.hasUpdateSuccess &&
              cndPrecheck.hasPayloadCNDAPIData &&
              cndPrecheck.hasFlightId &&
              cndPrecheck.isApproved &&
              cndPrecheck.hasWorkOrders &&
              cndPrecheck.hasDistrict &&
              cndPrecheck.isDroneCgi
            ) {
              try {
                const cndCallResult = await actions.CallCNDApi(payloadCNDAPIData);
                // Proxy returns raw CND response — success when no error key present
                const isSuccess =
                  !cndCallResult?.error && cndCallResult?.payload !== undefined;
                cndStatus = isSuccess ? 'Success' : 'Fail';
                cndResponse = isSuccess
                  ? cndCallResult?.payload
                  : { error: normalizeCndError(cndCallResult?.error ?? cndCallResult) };
              } catch (error) {
                cndResponse = {
                  error: normalizeCndError(error),
                };
                cndStatus = 'Error';
                // Surface runtime call failures for easier troubleshooting.
                // eslint-disable-next-line no-console
                console.error('CallCNDApi failed', error);
              }
            }
            // else {
            //   cndStatus = 'Skipped';
            //   cndResponse = {
            //     message:
            //       'CallCNDApi skipped because preconditions were not met',
            //     skipReasons,
            //     cndPrecheck,
            //     flightType,
            //     requestStatus: effectiveRequestStatus,
            //   };
            //   // eslint-disable-next-line no-console
            //   console.warn('CallCNDApi skipped', cndResponse);
            // }

            if (cndStatus !== '') {
              try {
                await actions.CreateCNDAPIAuditCallDetails({
                  payload: payloadCNDAPIData,
                  response: cndResponse,
                  apiResponseStatus: cndStatus,
                  requestId,
                });
              } catch (auditError) {
                // Keep save flow resilient even if audit write fails.
                // eslint-disable-next-line no-console
                console.error(
                  'CreateCNDAPIAuditCallDetails failed',
                  auditError
                );
              }
            }

            break;
          }

          case FlightCategory.DroneFlightExternal: {
            result = await actions.updateDroneExternalPUT(requestId, formData);

            break;
          }
        }

        if (result && result.payload) {
          ToastSuccess(Lang.MSG_REQUEST_UPDATE_SUCCESS);
          if (shouldReturn.current) {
            history.replace(
              state.path
                ? state.path
                : getRequestUrl({
                    ...data,
                    request: {
                      ...data.request,
                      requestId,
                    },
                  })
            );
          }
        } else {
          ToastError(
            result.error.message === 'ValidationError'
              ? result.error.error.errors.join('; ')
              : result.error.message
          );
        }
      }
    }, 1000),
    [
      requestId,
      isConfirm,
      actions,
      setIsConfirm,
      shouldReturn,
      state,
      cndDistrictState,
    ]
  );

  const handleDismiss = useCallback(() => {
    setIsPast(false);
  }, [setIsPast]);

  if (!state.data && status.fetching) {
    return <ATMLoader active />;
  }

  if (
    !state.data ||
    !isRequestAccessible(
      {
        requestId,
        requestStatus: state.data.request.requestStatus,
      },
      AccessType.FLIGHT_REQUEST_UPDATE
    )
  ) {
    return <Error404 />;
  }

  const updateStatus = [
    actionTypes.REQUEST_UPDATE_PATROL_PASSENGER_CONTRACTOR_DATA,
    actionTypes.REQUEST_UPDATE_PATROL_PASSENGER_DATA,
    actionTypes.REQUEST_UPDATE_CONSTRUCTION_DATA,
    actionTypes.REQUEST_UPDATE_CONSTRUCTION_CONTRACTOR_DATA,
    actionTypes.REQUEST_UPDATE_DRONE_INTERNAL_DATA,
    actionTypes.REQUEST_UPDATE_DRONE_EXTERNAL_DATA,
  ].some((actionType) => getRequestStatus(state, actionType).fetching);

  const buttons = (
    <>
      <ATMButton
        secondary
        onClick={() => history.goBack()}
        content={Lang.LBL_CLOSE}
      />
      <ATMButton
        primary
        content={Lang.LBL_SAVE}
        onClick={() => handleClick(false)}
        disabled={updateStatus}
        loading={updateStatus}
      />
      <ATMButton
        primary
        content={Lang.LBL_SAVE_CLOSE}
        onClick={() => handleClick(true)}
        disabled={updateStatus}
        loading={updateStatus}
      />
    </>
  );

  let { data } = state;

  if (data.request.requestId) {
    const duration =
      data.request && data.request.flightDuration
        ? parseFloat(data.request.flightDuration)
        : undefined;

    let flightDuration = data?.request?.flightDuration;

    if (
      isNaN(duration) &&
      typeof flightDuration === 'string' &&
      flightDuration.toLowerCase().includes('more')
    ) {
      flightDuration = 'more_than';
    } else if (typeof duration === 'number' && !isNaN(duration)) {
      flightDuration = duration.toString();
    }

    data = {
      ...data,
      request: {
        ...data.request,
        flightDuration,
      },
      flightInfo: data.flightInfo
        ? {
            ...data.flightInfo,
          }
        : null,
    };
  }

  return (
    <ATMContainer>
      <FlightRequestHeader data={state.data} requestId={requestId}>
        {buttons}
      </FlightRequestHeader>

      <FlightRequestForm
        formRef={formRef}
        defaultValues={data}
        handleSubmit={handleSubmit}
        isEdit
        isPast={isPast}
        handlePast={(value) => setIsPast(value)}
        handleDismiss={handleDismiss}
      />

      <FlightRequestChangeStatus
        open={!!isConfirm}
        loading={updateStatus}
        type={isConfirm?.request.requestStatus}
        onSubmit={(params) => {
          const user = getUser();
          const reasonBy = `${user?.first_name} ${user?.last_name}`;
          handleSubmit({
            ...isConfirm,
            request: {
              ...isConfirm?.request,
              reason: params.reason,
              reasonBy,
            },
          } as IRequestForm);
        }}
        onClose={() => setIsConfirm(undefined)}
      />

      <ATMGrid columns={1} className={styles.bottom}>
        <ATMGrid.Column textAlign="center" verticalAlign="top">
          {buttons}
        </ATMGrid.Column>
      </ATMGrid>
    </ATMContainer>
  );
};

export default FlightRequestEdit;
